import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ProgressBar, Text } from '@fluent/ui';
import type { ExamListening } from '@fluent/types';
import { audioUrl } from '../audio/audioUrl.js';
import { LISTENING_MAX_PLAYS, type ListeningState, type TaskAnswers } from './attempt.js';
import { McqQuestion, TaskHeader, paragraphsOf } from './parts.js';

/**
 * Task 1 — listening, the way the exam runs it: 40 seconds to read the
 * questions, then the recording twice. A play is spent the moment it
 * starts; there is no seeking and no third play. After the test (review)
 * the recording is free to replay and the transcript is shown.
 *
 * A plain HTMLAudioElement, not the Web Audio engine the lessons use: a
 * 2–3 minute clip should stream, not be decoded whole into memory.
 */

type Phase = 'preview' | 'ready' | 'playing' | 'done';

interface Props {
  task: ExamListening;
  answers: TaskAnswers;
  onPick: (index: number, letter: string) => void;
  state: ListeningState;
  onState: (next: ListeningState) => void;
  review: boolean;
}

function phaseOf(state: ListeningState, playing: boolean): Phase {
  if (playing) return 'playing';
  if (state.previewLeftMs > 0) return 'preview';
  if (state.playsUsed >= LISTENING_MAX_PLAYS) return 'done';
  return 'ready';
}

export function ListeningTask({ task, answers, onPick, state, onState, review }: Props) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  const phase = review ? 'done' : phaseOf(state, playing);

  // The 40-second look-through counts down only while this task is on
  // screen and the page is visible — the same rule as the exam clock.
  useEffect(() => {
    if (review || state.previewLeftMs <= 0) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const left = Math.max(0, stateRef.current.previewLeftMs - 1000);
      onStateRef.current({ ...stateRef.current, previewLeftMs: left });
    }, 1000);
    return () => window.clearInterval(id);
  }, [review, state.previewLeftMs > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the task, the test or the app stops the sound; that play stays
  // spent. (The exam clock stops when the app hides, so the recording must
  // not keep talking in the background either.)
  useEffect(() => {
    const stop = () => {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlaying(false);
      setProgress(0);
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);

  const play = (free: boolean) => {
    setFailed(false);
    audioRef.current?.pause();
    const el = new Audio(audioUrl(task.audio_key));
    audioRef.current = el;
    let counted = false;
    el.addEventListener('playing', () => {
      setPlaying(true);
      if (!free && !counted) {
        counted = true;
        onStateRef.current({ ...stateRef.current, playsUsed: stateRef.current.playsUsed + 1 });
      }
    });
    el.addEventListener('timeupdate', () => {
      if (el.duration > 0) setProgress(el.currentTime / el.duration);
    });
    el.addEventListener('ended', () => {
      setPlaying(false);
      setProgress(0);
    });
    el.addEventListener('error', () => {
      setPlaying(false);
      setFailed(true);
    });
    void el.play().catch(() => {
      setPlaying(false);
      setFailed(true);
    });
  };

  const previewSeconds = Math.ceil(state.previewLeftMs / 1000);
  const currentPlay = Math.min(LISTENING_MAX_PLAYS, state.playsUsed);

  return (
    <div className="fluent-exam__task">
      <TaskHeader n={1} />

      <div className="fluent-exam__player" data-phase={phase}>
        {review ? (
          <Button variant="secondary" size="md" fullWidth onClick={() => play(true)}>
            ▶ {t('exam.listen_review_play')}
          </Button>
        ) : phase === 'preview' ? (
          <div className="fluent-exam__preview">
            <span className="fluent-exam__preview-clock" aria-live="off">
              0:{String(previewSeconds).padStart(2, '0')}
            </span>
            <Text size="sm" weight="bold">
              {t('exam.listen_preview')}
            </Text>
            <ProgressBar value={1 - state.previewLeftMs / 40_000} tone="accent" size="sm" />
          </div>
        ) : phase === 'ready' ? (
          <Button variant="primary" size="lg" fullWidth onClick={() => play(false)}>
            ▶ {state.playsUsed === 0 ? t('exam.listen_play_1') : t('exam.listen_play_2')}
          </Button>
        ) : phase === 'playing' ? (
          <div className="fluent-exam__playing">
            <Text size="sm" weight="bold">
              🔊 {t('exam.listen_playing', { n: currentPlay })}
            </Text>
            <ProgressBar value={progress} tone="primary" size="md" />
          </div>
        ) : (
          <Text size="sm" weight="bold" tone="muted" align="center">
            ✓ {t('exam.listen_done')}
          </Text>
        )}
        {failed && (
          <div className="fluent-exam__player-error">
            <Text size="sm" tone="danger">
              {t('exam.listen_error')}
            </Text>
            <Button variant="neutral" size="sm" onClick={() => play(review)}>
              {t('exam.listen_retry')}
            </Button>
          </div>
        )}
      </div>

      {task.questions.map((q, i) => (
        <McqQuestion
          key={i}
          number={i + 1}
          question={q.question}
          options={q.options}
          picked={answers[i]}
          onPick={(letter) => onPick(i, letter)}
          review={review}
          correct={q.answer}
          evidence={q.evidence}
        />
      ))}

      {review && (
        <details className="fluent-exam__transcript">
          <summary>{t('exam.transcript')}</summary>
          <div lang="en">
            {paragraphsOf(task.transcript).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
